import {useEffect, useRef} from 'react'
import {Box, IconButton, Tooltip} from '@mui/material'
import {StyledButton, StyledInputBase} from '../CustomProjectDetailsStyles'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import PropTypes from 'prop-types'
import {hasDescendantCustomizations} from './helper'
import {
  StyledCustomTableRow,
  StyledCustomTableCell,
  StyledCustomTableCellInput,
  StyledCustomTableCellActions,
  StyledToolTip,
  StyledInfoIcon,
  StyledLabelText,
  StyledLabelTextContainer,
  StyledHistoryRoundedIcon,
  StyledEditIcon,
  StyledTitleExpandedButton,
  StyledDarkDot,
  StyledLightDot
} from './Styles'

const CustomTableRow = ({
  item,
  customLabels,
  handleCustomLabelChange,
  handleResetLabel,
  hasChildren,
  open,
  toggleOpen,
  level,
  isEditing,
  setEditingRowId,
  rowId,
  disableEdit,
  disableReset,
  error,
  errorRowId
}) => {
  const itemRowId = rowId || item.key
  const inputValue = customLabels[itemRowId]?.newValue || ''
  const hasCustomization = Boolean(inputValue?.trim())
  const paddingLeft = `${level * 20 + (hasChildren ? 0 : 14) + 4}px !important`
  const dotPosition = `${hasChildren ? -2 : -16}px !important`
  const inputRef = useRef(null)
  const isResettingRef = useRef(false)

  // Determine dot display logic:
  // 1. Dark dot: Current row has customization (highest priority)
  // 2. Light dot: Current row doesn't have customization BUT has children with customizations
  const shouldShowDarkDot = hasCustomization
  const shouldShowLightDot =
    !hasCustomization && hasChildren && hasDescendantCustomizations(item.children, customLabels)
  const shouldShowDot = shouldShowDarkDot || shouldShowLightDot

  const handleEditClick = (e) => {
    e.stopPropagation()
    if (!disableEdit && setEditingRowId) {
      setEditingRowId(itemRowId)
    }
  }

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing, itemRowId])

  const handleResetClick = (e) => {
    e.stopPropagation()
    isResettingRef.current = true
    handleResetLabel(itemRowId)
    if (setEditingRowId) {
      setEditingRowId(null)
    }
    setTimeout(() => {
      isResettingRef.current = false
    }, 100)
  }

  const handleInputClick = (e) => {
    e.stopPropagation()
  }
  const handleInputBlur = () => {
    if (isResettingRef.current) {
      return
    }
    setTimeout(() => {
      if (setEditingRowId) {
        setEditingRowId(null)
      }
    }, 150)
  }
  const handleInputKeyDown = (e) => {
    if ((e.key === 'Escape' || e.key === 'Enter') && setEditingRowId) {
      setEditingRowId(null)
    }
  }

  return (
    <StyledCustomTableRow highlightRow={item.highlightRow} hasChildren={hasChildren}>
      <StyledCustomTableCell paddingLeft={paddingLeft} onClick={hasChildren && !item.isTitle ? toggleOpen : undefined}>
        <Box display='flex' alignItems='center' position='relative'>
          {shouldShowDot && (
            <Box position='absolute' left={`${dotPosition}`} top='50%' sx={{transform: 'translateY(-50%)', zIndex: 1}}>
              {shouldShowDarkDot ? <StyledDarkDot /> : <StyledLightDot />}
            </Box>
          )}

          {item.isTitle && (
            <StyledTitleExpandedButton disabled aria-label='expand row' size='small'>
              <KeyboardArrowUpIcon />
            </StyledTitleExpandedButton>
          )}

          {!item.isTitle && hasChildren && (
            <IconButton aria-label='expand row' size='small'>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}

          <StyledLabelTextContainer>
            <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
              <StyledLabelText level={level} hasChildren={hasChildren}>
                {item.name}
              </StyledLabelText>
              {item.hasTooltip && (
                <StyledToolTip title={item.tooltipData} placement='right'>
                  <StyledInfoIcon />
                </StyledToolTip>
              )}
            </Box>
          </StyledLabelTextContainer>
        </Box>
      </StyledCustomTableCell>
      <StyledCustomTableCellInput hasError={Boolean(error && errorRowId === itemRowId)}>
        <StyledInputBase
          fullWidth
          value={inputValue}
          onChange={(e) => handleCustomLabelChange(itemRowId, e.target.value, item)}
          onClick={handleInputClick}
          disabled={!isEditing}
          inputRef={inputRef}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          isError={Boolean(error && errorRowId === itemRowId)}
        />
      </StyledCustomTableCellInput>
      <StyledCustomTableCellActions align='center'>
        <Box display='flex' justifyContent='center' alignItems='center'>
          <StyledButton
            data-testid='history-button'
            aria-label='reset-filter-name'
            onClick={handleResetClick}
            disabled={!inputValue || disableReset}
          >
            <Tooltip title='Revert to original Label' placement='top'>
              <StyledHistoryRoundedIcon variant={'primaryIcon'} />
            </Tooltip>
          </StyledButton>
          <StyledButton
            data-testid='edit-button'
            aria-label='edit-label'
            onClick={handleEditClick}
            disabled={disableEdit}
          >
            <Tooltip title='Edit Label' placement='top'>
              <StyledEditIcon variant='primaryIcon' />
            </Tooltip>
          </StyledButton>
        </Box>
      </StyledCustomTableCellActions>
    </StyledCustomTableRow>
  )
}

CustomTableRow.propTypes = {
  item: PropTypes.object.isRequired,
  level: PropTypes.number,
  customLabels: PropTypes.object,
  handleCustomLabelChange: PropTypes.func,
  handleResetLabel: PropTypes.func,
  hasChildren: PropTypes.bool,
  open: PropTypes.bool,
  toggleOpen: PropTypes.func,
  isEditing: PropTypes.bool,
  setEditingRowId: PropTypes.func,
  rowId: PropTypes.string,
  disableEdit: PropTypes.bool,
  disableReset: PropTypes.bool,
  error: PropTypes.string,
  errorRowId: PropTypes.string
}

export default CustomTableRow
